import { Typography, Box } from '@mui/material';
import { motion } from 'framer-motion';
import { PolygonalCard } from '../../../../containers/Cards/PolygonalCard/PolygonalCard';
import { FlexContainer } from '../../../../containers/ContentContainers/FlexContainer';
import { SimpleMetric } from '../../../../display/Metrics/SimpleMetric';
import { useTheme } from '@mui/material/styles';
import { UserReecapDto } from '@act/shared/data-transfer-objects';

interface WorkMetricsMobileProps {
  guideName: string;
  reecap: UserReecapDto;
}

export const WorkMetricsMobile = ({
  guideName,
  reecap,
}: WorkMetricsMobileProps) => {
  const theme = useTheme();

  const {
    callsThisYear = 0,
    membersServedThisYear = 0,
    needsAddressedThisYear = 0,
    needsIdentifiedThisYear = 0,
    pcpConnectionsThisYear = 0,
    textsThisYear = 0,
    visitsThisYear = 0,
  } = reecap;

  return (
    <FlexContainer
      direction="column"
      gap={8}
      alignItems="center"
      margin="40px 0"
      height="fit-content"
      width="100%"
      maxWidth="fit-content"
      overflowX="hidden"
      overflowY="auto"
    >
      {/* Mobile: Single paragraph */}
      <motion.div
        initial={{ opacity: 0, y: -25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.5 }}
        style={{
          textAlign: 'center',
        }}
      >
        <Typography
          component="p"
          variant="headlineLarge"
          fontSize="32px"
          fontWeight="500"
          color="primary.contrastText"
          lineHeight="32px"
        >
          This year you did phenomenal work{` `}
          <br />
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.75 }}
            style={{
              display: 'inline',
              color: theme.palette.primaryAccent.main,
            }}
          >
            {guideName}
          </motion.span>
        </Typography>
      </motion.div>
      {/* 1. Members impacted */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <PolygonalCard polygonName="hexagon" height="200px">
          <SimpleMetric
            iconName="User"
            metricValue={membersServedThisYear}
            metricSize="lg"
          />
        </PolygonalCard>
        <Typography
          variant="headlineLarge"
          fontWeight="500"
          color="primary.contrastText"
          textAlign="center"
        >
          Members impacted
        </Typography>
      </Box>
      {/* 2. PCP connections */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <PolygonalCard polygonName="pentagon" height="200px">
          <SimpleMetric
            iconName="PhoneCall"
            metricValue={pcpConnectionsThisYear}
            metricSize="lg"
          />
        </PolygonalCard>
        <Typography
          variant="headlineLarge"
          fontWeight="500"
          color="primary.contrastText"
          textAlign="center"
        >
          PCP connections
        </Typography>
      </Box>
      {/* 3. Needs identified */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <PolygonalCard polygonName="pentagon" height="200px">
          <SimpleMetric
            iconName="PhoneCall"
            metricValue={needsIdentifiedThisYear}
            metricSize="lg"
          />
        </PolygonalCard>
        <Typography
          variant="headlineLarge"
          fontWeight="500"
          color="primary.contrastText"
          textAlign="center"
        >
          Needs identified
        </Typography>
      </Box>
      {/* 4. Needs addressed */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <PolygonalCard polygonName="pentagon" height="200px">
          <SimpleMetric
            iconName="PhoneCall"
            metricValue={needsAddressedThisYear}
            metricSize="lg"
          />
        </PolygonalCard>
        <Typography
          variant="headlineLarge"
          fontWeight="500"
          color="primary.contrastText"
          textAlign="center"
        >
          Needs addressed
        </Typography>
      </Box>
      {/* 5. Calls made */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <PolygonalCard polygonName="pentagon" height="200px">
          <SimpleMetric
            iconName="PhoneCall"
            metricValue={callsThisYear}
            metricSize="lg"
          />
        </PolygonalCard>
        <Typography
          variant="headlineLarge"
          fontWeight="500"
          color="primary.contrastText"
          textAlign="center"
        >
          Calls made
        </Typography>
      </Box>
      {/* 6. Texts sent */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <PolygonalCard polygonName="tetrahedron_diamond" height="200px">
          <SimpleMetric
            iconName="ChatText"
            metricValue={textsThisYear}
            metricSize="lg"
          />
        </PolygonalCard>
        <Typography
          variant="headlineLarge"
          fontWeight="500"
          color="primary.contrastText"
          textAlign="center"
        >
          Texts sent
        </Typography>
      </Box>
      {/* 7. Home visits */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <PolygonalCard polygonName="heptagon" height="200px">
          <SimpleMetric
            iconName="HouseLine"
            metricValue={visitsThisYear}
            metricSize="lg"
          />
        </PolygonalCard>
        <Typography
          variant="headlineLarge"
          fontWeight="500"
          color="primary.contrastText"
          textAlign="center"
        >
          Home visits
        </Typography>
      </Box>
    </FlexContainer>
  );
};
